import { useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, DatabaseBackup, FileDown, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const Lista = ({ titulo, items, tone, testid }) => (
  <div data-testid={testid}>
    <div className={`text-[10px] uppercase tracking-widest font-black mb-1 ${tone}`}>{titulo}</div>
    <ul className="space-y-1">
      {items.map((m) => (
        <li key={m.modulo} className="text-sm border rounded px-3 py-2 bg-white">
          <b>{m.modulo}</b> <span className="text-zinc-600">— {m.detalle}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Pendientes = ({ rows }) => (
  <div className="overflow-x-auto max-h-96 border rounded" data-testid="rp-pendientes">
    <table className="w-full text-xs">
      <thead className="bg-zinc-100 sticky top-0 text-left">
        <tr>{["Pedido", "Cliente", "Envío", "Estado", "Incidencia", "Cant.", "F. entrega", "Saldo"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y">
        {rows.map((p) => (
          <tr key={p.numero}>
            <td className="px-2 py-1 font-bold">{p.numero}</td><td className="px-2 py-1">{p.cliente}</td>
            <td className="px-2 py-1">{p.envio}</td><td className="px-2 py-1">{p.estado}</td>
            <td className="px-2 py-1 text-red-700">{p.incidencias}</td><td className="px-2 py-1 tabular-nums">{p.cantidad}</td>
            <td className="px-2 py-1">{p.fecha_entrega}</td><td className="px-2 py-1 tabular-nums">{formatMoney(p.saldo)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ReportePrevio = ({ rango, onRespaldo }) => {
  const { desde, hasta } = rango;
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const cargar = async () => {
    setBusy(true);
    try { const { data } = await api.post("/cierres-mes/reporte-previo", rango); setRep(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const pdf = async () => {
    try {
      const { data } = await api.post("/cierres-mes/reporte-previo.pdf", rango, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      a.download = `reporte_previo_${desde}_${hasta}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const respaldo = async () => {
    setBusy(true);
    try { await api.post("/cierres-mes/respaldo"); toast.success("Respaldo creado"); onRespaldo?.(); await cargar(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const s = rep?.saldos; const v = rep?.verificacion;
  return (
    <Card className="p-4 space-y-4 border-2 border-[#002FA7]/30" data-testid="reporte-previo">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-black uppercase tracking-widest text-xs mr-auto">Verificación antes del cierre</div>
        <Button onClick={cargar} disabled={busy} variant="outline" data-testid="rp-generar"><ClipboardCheck className="w-4 h-4 mr-1" />Reporte previo</Button>
        <Button onClick={pdf} disabled={busy || !rep} variant="outline" data-testid="rp-pdf"><FileDown className="w-4 h-4 mr-1" />PDF</Button>
        <Button onClick={respaldo} disabled={busy} className="bg-zinc-900 hover:bg-black" data-testid="rp-respaldo"><DatabaseBackup className="w-4 h-4 mr-1" />Crear respaldo</Button>
      </div>
      {rep && (
        <>
          <div className="flex flex-wrap gap-2 text-xs" data-testid="rp-verificacion">
            <span className={`px-2 py-1 rounded font-bold ${v.respaldo ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`} data-testid="rp-respaldo-estado">
              {v.respaldo ? <><ShieldCheck className="inline w-3 h-3 mr-1" />Respaldo {v.respaldo.at.slice(0, 16).replace("T", " ")}</> : <><AlertTriangle className="inline w-3 h-3 mr-1" />Sin respaldo: el cierre está bloqueado</>}
            </span>
            <span className={`px-2 py-1 rounded font-bold ${v.cuadra ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`} data-testid="rp-cuadra">
              {v.cuadra ? "Ventas y Facturado coinciden con el Dashboard" : "No coincide con el Dashboard: revisar cuadre"}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Lista titulo="Quedarán en cero (se archivan)" items={rep.reinician} tone="text-red-700" testid="rp-reinician" />
            <Lista titulo="Conservan su información" items={rep.conservan} tone="text-emerald-700" testid="rp-conservan" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm" data-testid="rp-saldos">
            <div className="border rounded p-3"><div className="text-[10px] uppercase text-zinc-500">Pendientes de facturar</div><b>{s.pendientes_facturar_count} · {formatMoney(s.pendientes_facturar_monto)}</b></div>
            <div className="border rounded p-3"><div className="text-[10px] uppercase text-zinc-500">CxC total que pasa al nuevo mes</div><b>{formatMoney(s.cxc_total_trasladado)}</b></div>
            <div className="border rounded p-3"><div className="text-[10px] uppercase text-zinc-500">CxC del último cierre confirmado</div><b>{formatMoney(s.cxc_ultimo_cierre)}</b></div>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-black">Pedidos pendientes que se conservan ({rep.pendientes.length})</div>
          <Pendientes rows={rep.pendientes} />
        </>
      )}
    </Card>
  );
};
